import {
  withLruCache,
} from './lru-cache.ts';

/**
 * @param hex represents rgb hex with or without "hash", for example: 'fc1' '#fdbc12'
 * @returns array of numbers as 16 radix numbers representing "r" "g" and "b"
 */
export function hexToRgbNumberArray(hex: string): [number, number, number] {
  let cleanHex = hex.replace(/^#/, '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map((char) => char + char).join('');
  }
  const bigint = parseInt(cleanHex, 16);
  return [
    (bigint >> 16) & 255,
    (bigint >> 8) & 255,
    bigint & 255,
  ];
}

/**
 * Computes the WCAG relative luminance of a hex color.
 * Uses the IEC 61966-2-1 sRGB linearization threshold of 0.04045.
 * Results are memoized in an LRU cache (max 512 entries).
 *
 * @see https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
 *
 * @param hexColor - hex color string (with or without '#'), e.g. '#ff0000' or 'ff0000'
 * @returns luminance value between 0 (black) and 1 (white)
 */
export const computeColorLuminance = withLruCache((hexColor: string): number => {
  const [
    r,
    g,
    b,
  ] = hexToRgbNumberArray(hexColor);

  return 0.2126 * convertChannelToLinear(r / 255)
    + 0.7152 * convertChannelToLinear(g / 255)
    + 0.0722 * convertChannelToLinear(b / 255);
}, 512);

/**
 * Helper function to linearize an RGB color value
 *
 * @see https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
 * @param rgbChannel
 */
function convertChannelToLinear(rgbChannel: number): number {
  if (rgbChannel <= 0.04045) {
    return rgbChannel / 12.92;
  }
  return ((rgbChannel + 0.055) / 1.055) ** 2.4;
}

/**
 * Calculate the contras ratio according to WCAG 2.2
 *
 * @see https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio
 *
 * @param l1 - relative luminance of color 1
 * @param l2 - relative luminance of color 1
 */
function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const DARK_TEXT_COLOR = '#000000';
const BRIGHT_TEXT_COLOR = '#ffffff';

/**
 * Returns the matching font color for a given background color.
 *
 * It chooses the font color with the highest contrast.
 * This does not guarantee the minimum required contrast specified by W3C.
 *
 * @param color - hex color string (with or without '#'), e.g. '#ff0000'
 * @returns '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 * @see https://www.w3.org/TR/WCAG22/#contrast-minimum
 */
export const getContrastForRgbString = withLruCache((color: string) => {
  const colorLuminance = computeColorLuminance(color);
  const whiteLuminance = computeColorLuminance(BRIGHT_TEXT_COLOR);
  const blackLuminance = computeColorLuminance(DARK_TEXT_COLOR);

  const whiteContrast = contrastRatio(colorLuminance, whiteLuminance);
  const blackContrast = contrastRatio(colorLuminance, blackLuminance);

  return blackContrast > whiteContrast ? DARK_TEXT_COLOR : BRIGHT_TEXT_COLOR;
});
